import React from 'react';
import {
  Table,
  Switch,
  Tag,
  Typography,
  Popconfirm,
  Button,
  Tooltip,
  Space,
} from 'antd';
import { EyeTwoTone, EditOutlined, StopOutlined } from '@ant-design/icons';
import { IndexFund } from 'state';
import UsageCell, { SessionLite } from './UsageCell';

const { Text } = Typography;

/** Shared decommission-confirm copy (used by the table action + the drawer's Lifecycle section). */
export const DECOMMISSION_HINT =
  "Safe even if it's currently in use — sessions that already hold this fund keep computing " +
  "normally through to the end. It just can't be picked for any new session's benchmark.";

type Props = {
  funds: IndexFund[];
  loading?: boolean;
  sessionsById: Record<number, SessionLite>;
  onEdit: (fund: IndexFund) => void;
  onToggleEod: (fund: IndexFund, value: boolean) => void;
  onShowEod: (fund: IndexFund) => void;
  onDecommission: (fund: IndexFund) => void;
};

/**
 * Read-optimized index-fund table. Rows are NOT click-to-open — the drawer opens only via the Edit
 * (pencil) button; `fetch_eod` is a live inline toggle (one click, no edit mode). Description is
 * intentionally not a column (it lives in the drawer).
 */
const FundsTable: React.FC<Props> = ({
  funds,
  loading,
  sessionsById,
  onEdit,
  onToggleEod,
  onShowEod,
  onDecommission,
}) => {
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 64,
      render: (id: number) => <Text type="secondary">{id}</Text>,
    },
    {
      title: 'RIC',
      dataIndex: 'ric',
      key: 'ric',
      width: 150,
      render: (ric: string) => (
        <Text code style={{ whiteSpace: 'nowrap' }}>
          {ric}
        </Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Issuer',
      dataIndex: 'issuer_name',
      key: 'issuer_name',
      width: 170,
      ellipsis: true,
    },
    {
      title: 'Asset class',
      dataIndex: 'asset_class',
      key: 'asset_class',
      width: 130,
      render: (v?: string) =>
        v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Geography',
      dataIndex: 'geography',
      key: 'geography',
      width: 130,
      render: (v?: string) =>
        v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Fetch EoD',
      dataIndex: 'fetch_eod',
      key: 'fetch_eod',
      width: 100,
      render: (val: boolean, fund: IndexFund) => (
        <Switch
          size="small"
          checked={val}
          onChange={checked => onToggleEod(fund, checked)}
        />
      ),
    },
    {
      title: 'Usage',
      key: 'usage',
      width: 130,
      render: (_: unknown, fund: IndexFund) => (
        <UsageCell fund={fund} sessionsById={sessionsById} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, fund: IndexFund) => (
        <Space size={0}>
          <Tooltip title="Show EoD">
            <Button
              type="text"
              icon={<EyeTwoTone />}
              onClick={() => onShowEod(fund)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(fund)}
            />
          </Tooltip>
          <Popconfirm
            title="Decommission this fund?"
            description={
              <div style={{ maxWidth: 300 }}>{DECOMMISSION_HINT}</div>
            }
            okText="Decommission"
            onConfirm={() => onDecommission(fund)}
          >
            <Tooltip title="Decommission">
              <Button type="text" danger icon={<StopOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // antd sizes the inner <table> to width:100%, so the table always spans its container. Override
  // it to content width (max-content) via a scoped rule; the wrapper stays full-width with
  // overflowX:auto so a table wider than the viewport scrolls instead of overflowing the page.
  return (
    <div className="ifv2-fit-table" style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <style
        dangerouslySetInnerHTML={{
          __html:
            '.ifv2-fit-table .ant-table{width:max-content}' +
            '.ifv2-fit-table .ant-table table{width:auto}',
        }}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={funds}
        columns={columns as never}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />
    </div>
  );
};

export default FundsTable;
